import React, { useState } from 'react';
import logger from '../utils/logger';

const AddToProjectsButton = ({ 
  measure, 
  assessmentId, 
  onSuccess, 
  className = "",
  variant = "primary" 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleAddToProjects = async () => {
    setIsLoading(true);
    try {
      // Use the new comprehensive API endpoint
      const projectData = {
        account_id: 'account-123', // Demo account - in real app, get from auth
        measure_id: measure.id,
        assessment_id: assessmentId,
        title: measure.title,
        estimated_savings_aud: measure.savings_potential,
        estimated_cost_aud: measure.implementation_cost,
        payback_years: measure.payback_years,
        roi_pct: measure.implementation_cost > 0 ? (measure.savings_potential / measure.implementation_cost) * 100 : 0,
        priority: measure.payback_years <= 2 ? 'high' : measure.payback_years <= 4 ? 'medium' : 'low',
        meta: {
          source: 'ESA',
          confidence: measure.confidence_level,
          category: measure.category
        }
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/from-opportunity`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(projectData)
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setShowConfirmation(true);
        
        // Auto-hide confirmation after 3 seconds
        setTimeout(() => {
          setShowConfirmation(false);
        }, 3000);
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        logger.error('Failed to add measure to projects');
        // Fallback to legacy endpoint
        const legacyResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/from-measure?measure_id=${measure.id}&assessment_id=${assessmentId}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (legacyResponse.ok) {
          const data = await legacyResponse.json();
          setShowConfirmation(true);
          setTimeout(() => setShowConfirmation(false), 3000);
          if (onSuccess) onSuccess(data);
        }
      }
    } catch (error) {
      logger.error('Error adding measure to projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonStyles = () => {
    const baseStyles = "inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
    
    switch (variant) {
      case 'primary':
        return `${baseStyles} bg-blue-600 text-white hover:bg-blue-700 ${className}`;
      case 'secondary':
        return `${baseStyles} bg-gray-200 text-gray-700 hover:bg-gray-300 ${className}`;
      case 'outline':
        return `${baseStyles} border border-blue-600 text-blue-600 hover:bg-blue-50 ${className}`;
      case 'minimal':
        return `${baseStyles} text-blue-600 hover:bg-blue-50 ${className}`;
      default:
        return `${baseStyles} bg-blue-600 text-white hover:bg-blue-700 ${className}`;
    }
  };

  return (
    <>
      <button
        onClick={handleAddToProjects}
        disabled={isLoading}
        className={getButtonStyles()}
        title="Add this measure to your Projects for tracking and implementation"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Adding...
          </>
        ) : (
          <>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add to Projects
          </>
        )}
      </button>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-8 border w-96 shadow-lg rounded-md bg-white mx-4">
            <div className="text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
              
              {/* Message */}
              <p className="text-sm text-gray-600 mb-6">
                This measure has been added to your Projects. You can now track its implementation and assign partners.
              </p>
              
              {/* Measure Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-medium text-gray-900 mb-2">{measure.title}</h4>
                {measure.savings_potential && (
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Annual Savings:</span> ${measure.savings_potential.toLocaleString()}
                  </p>
                )}
                {measure.payback_years && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Payback Period:</span> {measure.payback_years} years
                  </p>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
                <a
                  href="/projects"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  View Projects
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddToProjectsButton;